import * as React from 'react';
import { styled } from '@linaria/react';
import { Check } from 'lucide-react';
import useSound from 'use-sound';

import { sampleOne } from '@/utils';

interface Props extends React.HTMLAttributes<HTMLLabelElement> {
  color: string;
  label: string;
  isEnabled: boolean;
  onToggle: (newValue: boolean) => void;
}

function CheckboxBtn({
  color,
  label,
  isEnabled,
  onToggle,
  ...delegated
}: Props) {
  const [playTick] = useSound('/sounds/rubber-button-sprite.mp3', {
    sprite: SPRITES,
    volume: 0.5,
  });

  return (
    <Wrapper
      data-is-enabled={isEnabled}
      {...delegated}
      onPointerDown={() => {
        playTick({
          id: sampleOne(Object.keys(SPRITES).slice(0, 3)),
        });
      }}
      onPointerUp={() =>
        playTick({ id: sampleOne(Object.keys(SPRITES).slice(3)) })
      }
    >
      <Face>
        <PseudoCheckbox>
          {isEnabled && (
            <Check size={16} strokeWidth={4} color="black" />
          )}
        </PseudoCheckbox>
        <HiddenCheckbox
          type="checkbox"
          checked={isEnabled}
          onChange={(ev) => {
            onToggle(ev.target.checked);
          }}
        />
        <Label>{label}</Label>
      </Face>
    </Wrapper>
  );
}

const SPRITES: Record<string, [number, number]> = {
  down1: [250, 150],
  down2: [450, 150],
  down3: [650, 150],
  up1: [1050, 150],
  up2: [1250, 150],
  up3: [1450, 150],
};

const Wrapper = styled.label`
  border-radius: 4px;
  background: hsl(210deg 15% 18%);
  cursor: pointer;

  &[data-is-enabled='true'] {
    background: hsl(175deg 85% 15%);
  }

  &:has(input:focus-visible) {
    outline: 3px auto hsl(175deg 100% 65%);
    outline-offset: 3px;
  }
`;

const Face = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem;
  border-radius: inherit;
  transform: translateY(-2px);
  /* Hide the glow near the top: */
  overflow: hidden;

  &:has(input:active) {
    transform: translateY(0);
  }

  ${Wrapper}[data-is-enabled='true'] & {
    background: linear-gradient(
      0deg,
      hsl(170deg 70% 25%),
      hsl(150deg 70% 42%)
    );

    &::before {
      content: '';
      position: absolute;
      top: -6px;
      left: 4px;
      right: 4px;
      height: 12px;
      border-radius: 0 0 100% 100%;
      background: hsl(150deg 100% 80%);
      filter: blur(3px);
      opacity: 0.6;
      transition: opacity 250ms ease-in-out;
    }
  }
  ${Wrapper}[data-is-enabled='false'] & {
    background: linear-gradient(
      0deg,
      hsl(210deg 15% 25%),
      hsl(210deg 12% 30%)
    );
  }

  html[data-color-mode='light'] ${Wrapper}[data-is-enabled='true'] & {
    &::before {
      opacity: 0.2;
    }
  }
`;

const PseudoCheckbox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 4px;
  background: hsl(0deg 0% 80%);
  box-shadow:
    inset 0px 1px 2px hsl(0deg 0% 0% / 0.4),
    inset 0px 3px 6px hsl(0deg 0% 0% / 0.3);

  ${Wrapper}[data-is-enabled='true'] & {
    background: white;
    box-shadow: 0px 1px 0px hsl(0deg 0% 0% / 0.25);
  }
`;

const HiddenCheckbox = styled.input`
  position: absolute;
  opacity: 0;
`;

const Label = styled.div`
  font-size: 0.875rem;
  font-weight: var(--font-weight-medium);
  color: var(--color-text);

  color: white;
`;

export default CheckboxBtn;
